import type { CallScriptHistoryOf } from "./callScriptShared.types";

// ---------------------------------------------------------------------------
// callScriptResidence.types
//
// Field model + history shapes for the residence ("Customer Verification")
// Call Script. Mirrors callScript.types.ts, which does the same job for the
// employment script.
//
// NOTE: there is no BFF endpoint for this script yet — the generated client
// (@credit-acceptance/step6-bff-client 1.0.780) exposes only the employment
// trio (getHistory1 / saveCallScript / getVerificationQuestions). These shapes
// are modelled on the employment contract plus CAPS2's
// Step6ApplicantResidenceVerificationModelBean, and are served by
// callScriptResidence.mock.ts until the real endpoints land. Expect to
// reconcile field names against the real swagger at that point.
// ---------------------------------------------------------------------------

/** Residence-verification fields captured during a Customer Verification call. */
export interface ResidenceVerifiedFields {
  // Contact block
  phoneNumberDialed: string;
  wasOriginalNumberBad: boolean;
  dateOfBirth: string;
  primaryNumber: string;
  secondaryNumber: string;
  consentToSignElectronically: string;
  signedCopyProvided: string;

  // Physical address
  physicalStreetNumber: string;
  physicalStreetName: string;
  physicalApartmentNumber: string;
  physicalZip: string;
  physicalCity: string;
  physicalState: string;

  // Mailing address
  mailingStreetNumber: string;
  mailingStreetName: string;
  mailingApartmentNumber: string;
  mailingZip: string;
  mailingCity: string;
  mailingState: string;

  // Deal / down payment block
  whoIsPrimaryDriver: string;
  tradeIn: string;
  totalCashAndTradeIn: number | "";
  anyAmountStillOwed: string;
  whenIsRemainingDue: string;
  grossTradeInAmount: number | "";
  tradeInPayoffAmount: number | "";
  cashDownPaidOnDelivery: number | "";
  amountDueAfterDelivery: number | "";
  whenIsFinalAmountDue: string;
}

export type ResidenceFieldKey = keyof ResidenceVerifiedFields;

/** Blank "Verified Information" — what the analyst captures live on the call, never a copy of saved data. */
export const EMPTY_RESIDENCE_VERIFIED_FIELDS: ResidenceVerifiedFields = {
  phoneNumberDialed: "",
  wasOriginalNumberBad: false,
  dateOfBirth: "",
  primaryNumber: "",
  secondaryNumber: "",
  consentToSignElectronically: "",
  signedCopyProvided: "",
  physicalStreetNumber: "",
  physicalStreetName: "",
  physicalApartmentNumber: "",
  physicalZip: "",
  physicalCity: "",
  physicalState: "",
  mailingStreetNumber: "",
  mailingStreetName: "",
  mailingApartmentNumber: "",
  mailingZip: "",
  mailingCity: "",
  mailingState: "",
  whoIsPrimaryDriver: "",
  tradeIn: "",
  totalCashAndTradeIn: "",
  anyAmountStillOwed: "",
  whenIsRemainingDue: "",
  grossTradeInAmount: "",
  tradeInPayoffAmount: "",
  cashDownPaidOnDelivery: "",
  amountDueAfterDelivery: "",
  whenIsFinalAmountDue: "",
};

/**
 * The CAPS2 verification-question text for each expirable field, taken from
 * `showAndHideTheFields()`'s expired-question matching. Doubles as the
 * `questionText` written onto saved history answers, so the history panel
 * renders the same wording the legacy screen did.
 *
 * The five unconditional down-payment amounts have no question counterpart in
 * CAPS2 and so are absent here — they ride the save body as typed fields only.
 */
export const RESIDENCE_CALL_SCRIPT_QUESTION_TEXT = {
  phoneNumberDialed: "Phone Number Dialed",
  wasOriginalNumberBad: "Was original number bad?",
  dateOfBirth: "Date of Birth(Required for RPC)",
  primaryNumber: "Primary Number",
  secondaryNumber: "Secondary Number",
  consentToSignElectronically: "Consent to sign electronically",
  signedCopyProvided: "Was a Signed copy of the contract provided by the Dealer?",
  physicalStreetNumber: "Physical Address Street No",
  physicalStreetName: "Physical Address Street Name",
  physicalApartmentNumber: "Physical Address Apt",
  physicalZip: "Physical Address Zip",
  physicalCity: "Physical Address City",
  physicalState: "Physical Address State",
  mailingStreetNumber: "Mailing Address Street No",
  mailingStreetName: "Mailing Address Street Name",
  mailingApartmentNumber: "Mailing Address Apt",
  mailingZip: "Mailing Address Zip",
  mailingCity: "Mailing Address City",
  mailingState: "Mailing Address State",
  whoIsPrimaryDriver: "Who is the Primary Driver?",
  tradeIn: "Trade-in(Y/N)",
  totalCashAndTradeIn: "Total Cash & Trade-in Paid on Delivery?",
  anyAmountStillOwed: "Any Amount still owed?",
  whenIsRemainingDue: "When is the remaining due?",
} as const satisfies Partial<Record<ResidenceFieldKey, string>>;

/** Fields that carry a CAPS2 verification question (and so appear in `verificationAnswers[]`). */
export type ResidenceQuestionFieldKey = keyof typeof RESIDENCE_CALL_SCRIPT_QUESTION_TEXT;

/** One per-question verified-vs-saved comparison on a history entry. */
export interface ResidenceCallScriptHistoryAnswer {
  verificationAnswerPk: number;
  /** Absent until a residence question catalog endpoint exists (the employment script resolves these from `getVerificationQuestions`). */
  verificationQuestionPk?: number;
  questionText: string;
  answerEntered: string;
  answerStored: string;
  isAnswerMatch: "Y" | "N";
}

/** A saved Customer Verification call. */
export interface ResidenceCallScriptEntry {
  verificationPhoneCallPk: number;
  phoneNumberDialed: string;
  /** ISO datetime the call occurred. */
  occurredAt: string;
  analystName: string;
  applicantAddressPk?: number;
  answers: ResidenceCallScriptHistoryAnswer[];
}

export type ResidenceCallScriptHistory = CallScriptHistoryOf<ResidenceCallScriptEntry>;
